import React, { Component, CSSProperties, ReactElement, ReactNode } from 'react'
import { observer } from 'mobx-react'
import { Select } from 'antd'
import { OptionProps } from 'antd/lib/select'
import { DiscountStore } from './DiscountStore'
import { DataSourceItemObject } from 'antd/lib/auto-complete'
import { getPopupContainer } from '../../../../../utilities/helpers'

interface Props {
  value?: string
  onSelect: (value: string) => void
  style?: CSSProperties
  placeholder?: string | ReactNode
  notFoundContent?: string
  isSubCategorySelect?: boolean
  discountStore: DiscountStore
  discountOption: 'include' | 'exclude'
  disableOption: 'selected' | 'none'
}

@observer
export class SelectDiscountCategories extends Component<Props> {
  shouldChildCategoryDisabled = (dataSourceItemObject: DataSourceItemObject) => {
    const selectedCategories =
      this.props.discountOption === 'include'
        ? this.props.discountStore.includedCategories
        : this.props.discountStore.excludedCategories
    const matchedBaseCategory = this.props.discountStore.mappedFlattenedCategories.get(
      dataSourceItemObject.value,
    )

    if (matchedBaseCategory && matchedBaseCategory.parentCategoryId) {
      const isMasterSelected = selectedCategories.find(
        (category) => category.id === matchedBaseCategory.parentCategoryId,
      )

      if (isMasterSelected) return true
    }

    return !!selectedCategories.find((categoryId) => categoryId.id === dataSourceItemObject.value)
  }

  filterOption = (input: string, option: ReactElement<OptionProps>) => {
    const optionName = option.props.label

    if (optionName && typeof optionName === 'string') {
      const lowerCaseInput = input.toLowerCase().trim()

      return optionName.toLowerCase().indexOf(lowerCaseInput) >= 0
    }

    return true
  }

  get categoryDataSource(): DataSourceItemObject[] {
    const categories = [...this.props.discountStore.mappedFlattenedCategories.values()]

    const dataSourceItemObject = categories.map((category) => {
      const parentCategory = category.parentCategoryId
        ? this.props.discountStore.mappedFlattenedCategories.get(category.parentCategoryId)
        : undefined

      const categoryName = parentCategory
        ? `${parentCategory.name} - ${category.name}`
        : category.name

      const DataSourceItemObject = {
        value: category.id,
        text: categoryName,
      }

      return DataSourceItemObject
    })

    return dataSourceItemObject
  }

  get subCategoryDataSource() {
    const mappedCategories = this.props.discountStore.mappedFlattenedCategories
    const includedCategories = new Map()
    const excludedCategories = new Map()

    this.props.discountStore.includedCategories.forEach((category) => {
      includedCategories.set(category.id, category)
    })

    this.props.discountStore.excludedCategories.forEach((category) => {
      excludedCategories.set(category.id, category)
    })

    const selectableSubCategories = [...mappedCategories.values()].filter((category) =>
      includedCategories.get(category.parentCategoryId),
    )

    // Filter out included sub-category and excluded sub-category
    const filteredSelectableSubCategories = selectableSubCategories.filter((subCategory) => {
      const isIncluded = !!includedCategories.get(subCategory.id)
      const isExcluded = !!excludedCategories.get(subCategory.id)

      return !isIncluded && !isExcluded
    })

    const subCategoryDataSource = filteredSelectableSubCategories.map((category) => {
      const parentCategory = category.parentCategoryId
        ? mappedCategories.get(category.parentCategoryId)
        : undefined

      const categoryName = parentCategory
        ? `${parentCategory.name} - ${category.name}`
        : category.name

      return {
        value: category.id,
        text: categoryName,
      }
    })

    return subCategoryDataSource
  }

  render() {
    const dataSource = this.props.isSubCategorySelect
      ? this.subCategoryDataSource
      : this.categoryDataSource

    return (
      <Select<string>
        showSearch
        allowClear
        value={this.props.value}
        onSelect={this.props.onSelect}
        style={{ flex: 1, ...this.props.style }}
        placeholder={this.props.placeholder ? this.props.placeholder : 'Select a category'}
        notFoundContent={this.props.notFoundContent}
        dropdownMatchSelectWidth={false}
        filterOption={this.filterOption}
        getPopupContainer={getPopupContainer}
      >
        {dataSource.map((category) => {
          const baseCategory = this.props.discountStore.mappedFlattenedCategories.get(
            category.value,
          )

          if (!baseCategory) return

          const categoryColor = baseCategory.colorHex || 'gray'
          const categoryIconStyle = baseCategory.parentCategoryId
            ? { width: 12, height: 12, marginLeft: 16 }
            : { width: 16, height: 16 }

          return (
            <Select.Option
              key={category.value}
              label={category.text}
              style={{ whiteSpace: 'normal' }}
              className='discount-select-options'
              disabled={
                this.props.disableOption === 'selected' &&
                this.shouldChildCategoryDisabled(category)
              }
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span
                  style={{
                    ...categoryIconStyle,
                    backgroundColor: categoryColor,
                    marginRight: 4,
                    flex: 'none',
                  }}
                />
                {category.text}
              </div>
            </Select.Option>
          )
        })}
      </Select>
    )
  }
}
